@use '~/vendor/cxy/scss/styles/index.scss' as cxy;

.header {
  margin: 0;
}

.splitHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.iconSlot {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
}

.heading {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  align-items: center;
}

.toggle {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 0;
  background-color: transparent;
  border: none;
}

.triggerElement {
  cursor: pointer;

  /* `flex-shrink` and the rotation transition belong to the design system's chevron, not to each
     consumer: every call site that drew its own glyph re-declared both, with four different
     durations. */
  .indicator {
    flex-shrink: 0;
    pointer-events: none;
    transition: transform 200ms ease;
  }

  &[data-state='open'] {
    .indicator {
      transform: rotate(180deg);
      transform-origin: center;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .triggerElement .indicator {
    transition: none;
  }
}

/* Without an author colour the button takes the UA's, which iOS Safari makes `-apple-system-blue`.
   Zero specificity so a variant or a caller's single class still wins whatever the load order. */
:where(.trigger, .toggle) {
  color: inherit;
}

/* The chevron takes the row's text colour. The icon set's own default is `white`, which is why no
   consumer on a light surface could use the built-in one. Scoped to `Accordion.Item`'s header rather
   than to `.triggerElement`: a headless `useToggle` caller renders the indicator in markup of its
   own, and was built against that white. */
.trigger .indicator,
.toggle .indicator {
  color: inherit;
}

/* After the rule above on purpose — same weight, so source order settles it, and this theme's light
   nav surfaces want the glyph dark whatever the row inherits. */
[data-fpsm-theme='gazzetta'] {
  .indicator {
    color: var(--cxy-color-icon-black);
  }
}

// Deliberately no `padding`: a `bare` caller's row padding is a single class in ANOTHER stylesheet,
// so an equal-specificity rule here would be settled by load order rather than by intent. The UA
// default loses to any call-site class, which is the behaviour a `bare` consumer wants.
.trigger {
  margin: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background-color: transparent;
  border: none;
}

.contentElement {
  &[data-state='closed'] {
    display: block;
    visibility: hidden;
  }
}

/* The enter/exit fade, so a caller that asks for a duration does not also have to write the
   transition — four module stylesheets carried an identical copy of it before NGF-7074.
   `data-animated` gates it: with no duration the panel never gets `data-enter`, and an ungated
   `opacity: 0` would leave it permanently invisible. Both classes, because `Accordion.Item` sets its
   own `className` over the one `useToggle` gives a headless caller's panel. */
.content[data-animated],
.contentElement[data-animated] {
  opacity: 0;
  transition: opacity var(--cxy-accordion-duration, 0s) ease;

  &[data-enter] {
    opacity: 1;
  }
}

// Colour belongs to a variant, not to the structure: `bare` renders on surfaces the design system
// does not own (the dark burger menu, the bottom nav), where a content colour is wrong.
.primary,
.secondary {
  .trigger,
  .toggle,
  .content {
    color: var(--cxy-color-text-default-body);
  }
}

.item {
  &.dense {
    .trigger,
    .content {
      padding-inline: 0;
    }
  }
}

.primary {
  .item {
    &:not(:first-of-type) {
      padding-block-start: var(--cxy-spacing-xxsm);
    }

    &:not(:last-of-type) {
      border-block-end: var(--cxy-border-default);
    }
  }

  .trigger {
    @include cxy.font-size('subHeading-s-mobile');
    padding-block: var(--cxy-spacing-xxsm);
    padding-inline: var(--cxy-spacing-xsm);

    @include cxy.screen-size('desktop') {
      @include cxy.font-size('subHeading-s');
      padding-inline: 0;
    }
  }

  .content {
    padding-block-end: var(--cxy-spacing-5);
    padding-inline: var(--cxy-spacing-xsm);

    @include cxy.font-size('text-s');

    @include cxy.screen-size('desktop') {
      @include cxy.font-size('text-m', var(--cxy-fontWeight-normal));
      padding-inline: 0;
    }
  }
}

.secondary {
  .item {
    background-color: color-mix(in srgb, var(--cxy-color-surface-page), transparent 88%);
    border-radius: var(--cxy-border-radius-m);
    padding: var(--cxy-spacing-xsm) var(--cxy-spacing-5);

    @include cxy.screen-size('desktop') {
      padding: var(--cxy-spacing-xsm) 50px;
    }

    + .item {
      margin-block-start: var(--cxy-spacing-3);
    }

    .triggerElement {
      padding: 0;

      svg {
        height: var(--cxy-icon-size-xxsm);
        width: var(--cxy-icon-size-xxsm);
      }
    }

    .trigger {
      font-size: var(--cxy-fontSize-s);
      line-height: var(--cxy-lineHeight-m);
      font-weight: var(--cxy-fontWeight-bold);

      @include cxy.screen-size('desktop') {
        font-size: var(--cxy-fontSize-l);
        line-height: 44px;
      }
    }

    .content {
      font-size: var(--cxy-fontSize-s);
      font-weight: var(--cxy-fontWeight-normal);
      line-height: var(--cxy-lineHeight-xs);
      padding-block-start: var(--cxy-spacing-3);

      @include cxy.screen-size('desktop') {
        line-height: var(--cxy-lineHeight-l);
        padding-block: 0 var(--cxy-spacing-3);
      }
    }
  }
}

[data-fpsm-theme='gazzetta'] {
  .secondary {
    .item {
      background-color: var(--cxy-color-surface-page);
    }
  }
}

/* The dark disclosure row of a navigation drawer or sports list. Written against the ITEM, not the
   group, so a standalone `Accordion.Item` carries it — a drawer renders each row independently
   between other row types, with no group to wrap them in. */
.nav {
  display: flex;
  flex-direction: column;

  .trigger,
  .link {
    box-sizing: border-box;
    display: flex;
    gap: var(--cxy-spacing-xxsm);
    align-items: center;
    width: 100%;
    min-height: 48px;
    padding: var(--cxy-spacing-3) var(--cxy-spacing-xsm);
    color: var(--cxy-color-content-on-inverse);
    text-align: left;
  }

  /* The divider is an inset shadow rather than a border so its 1px does not push the row past 48px,
     which is what the design's inside stroke means. */
  .trigger {
    background: transparent;
    border: 0;
    box-shadow: inset 0 -1px 0 var(--cxy-color-border-on-inverse);
    cursor: pointer;
  }

  .link {
    background-color: var(--cxy-color-border-on-inverse);
    box-shadow: inset 0 -1px 0 var(--cxy-color-border-inverse-strong);
    text-decoration: none;
  }

  .iconSlot {
    width: 24px;
    height: 24px;
    /* Kills the descender a consumer's icon wrapper adds, which rides the glyph high in the row. */
    line-height: 0;
  }

  .headingText,
  .linkLabel {
    display: block;
    flex: 1 0 0;
    min-width: 0;
    margin: 0;
    color: var(--cxy-color-content-on-inverse);
    font-weight: var(--cxy-fontWeight-semiBold);
    font-size: var(--cxy-fontSize-m);
    line-height: var(--cxy-lineHeight-l);
    text-transform: capitalize;
    overflow-wrap: break-word;
  }

  /* Direct child only: the icon slot also holds an svg, and it must not rotate with the chevron.
     A caller-supplied indicator gets no class of ours, so position is the only handle. */
  .trigger > svg,
  .toggle > svg {
    flex-shrink: 0;
    transition: transform 200ms ease;
  }

  [data-state='open'] > svg {
    transform: rotate(180deg);
    transform-origin: center;
  }

  .linkList {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  /* The panel behind caller-supplied rows. A `links` list sets the same colour per row, so the two
     panel shapes match. */
  .content {
    background-color: var(--cxy-color-border-on-inverse);
  }
}

@media (prefers-reduced-motion: reduce) {
  .nav {
    .trigger > svg,
    .toggle > svg {
      transition: none;
    }
  }
}
